import type { ActionTypeId, OutreachSource } from '@seoplum/core'
import { costProviderLabel, costProviderSlot } from '@seoplum/core'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from './api'
import type { DateRange } from './queries'
import { seriesColor } from './scale'
import { rangeDays } from './site-data'

/** What we bought, who we bought it for, what it was bought about. */
export const COST_DIMENSIONS = ['provider', 'organization', 'site'] as const

export type CostDimension = (typeof COST_DIMENSIONS)[number]

/** The workspace is the tenant and the site is the domain; between them they
 * are what anyone means by "per project", so both are offered rather than one
 * being guessed at. */
export const DIMENSION_LABELS: Record<CostDimension, string> = {
  provider: 'Provider',
  organization: 'Workspace',
  site: 'Site',
}

export interface CostFilters {
  provider?: string
  organizationId?: string
  siteId?: string
}

export interface CostBucket {
  start: string
  end: string
  days: number
  /** Only ever true of the oldest bar — buckets are measured back from the end
   * of the range, so a young week never draws short. */
  partial: boolean
  cost: number
  values: Record<string, number>
}

export interface DimensionTotal {
  key: string
  label: string
  cost: number
  events: number
  quantity: number
  failed: number
}

export interface Facet {
  key: string
  label: string
}

export interface CostData {
  range: { from: string; to: string }
  previousRange: { from: string; to: string }
  bucketDays: number
  dimension: CostDimension
  filters: CostFilters
  earliest: string | null
  total: { cost: number; previous: number }
  breakdown: DimensionTotal[]
  facets: { providers: Facet[]; organizations: Facet[]; sites: Facet[] }
  buckets: CostBucket[]
}

export function useCosts(range: DateRange, dimension: CostDimension, filters: CostFilters) {
  return useQuery({
    queryKey: ['admin', 'costs', range, dimension, filters],
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    // A 404 here is the staff gate, not a blip. Retrying it just delays the
    // route's own redirect by three round trips.
    retry: false,
    queryFn: async () => {
      const response = await api.api.admin.costs.$get({
        query: {
          days: String(rangeDays(range)),
          groupBy: dimension,
          ...(filters.provider ? { provider: filters.provider } : {}),
          ...(filters.organizationId ? { organizationId: filters.organizationId } : {}),
          ...(filters.siteId ? { siteId: filters.siteId } : {}),
        },
      })
      return unwrap<CostData>(response)
    },
  })
}

export interface CostBand {
  key: string
  label: string
  color: string
}

/**
 * Spend the chosen dimension cannot account for: platform work belonging to no
 * tenant, or a workspace that has since been deleted. Named rather than hidden,
 * because spend nobody owns is the kind worth noticing — and it is a real key
 * rather than a blank, so it can be filtered to like any other.
 */
export const UNATTRIBUTED = 'none'

export function isUnattributed(key: string): boolean {
  return key === UNATTRIBUTED
}

export function rowLabel(dimension: CostDimension, row: { key: string; label: string }): string {
  if (isUnattributed(row.key)) return 'Unattributed'
  return dimension === 'provider' ? costProviderLabel(row.key) : row.label || row.key
}

/**
 * Colour follows the entity, never its rank.
 *
 * A provider takes its fixed slot in the registry, so a month where the SERP
 * feed outspends the backlink one does not swap their bands. Workspaces and
 * sites have no registry, so the slot follows the name alphabetically among the
 * eight largest — the same rule the estate chart uses, and for the same reason:
 * re-ranking must not repaint. Past eight the tail falls to the neutral, since
 * a ninth invented hue is one nobody can tell from the third.
 */
export function costBands(dimension: CostDimension, breakdown: DimensionTotal[]): CostBand[] {
  const labelled = breakdown.map((row) => ({ ...row, name: rowLabel(dimension, row) }))

  if (dimension === 'provider') {
    return labelled.map((row) => ({
      key: row.key,
      label: row.name,
      color: seriesColor(isUnattributed(row.key) ? 8 : costProviderSlot(row.key)),
    }))
  }

  const hued = new Map(
    [...labelled]
      .slice(0, 8)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((row, index) => [row.key, index]),
  )

  return labelled.map((row) => ({
    key: row.key,
    label: row.name,
    color: seriesColor(hued.get(row.key) ?? 8),
  }))
}

/**
 * Operational state, which exists only here.
 *
 * None of these have a tenant-facing counterpart on purpose: how often we
 * collect, how far behind we are and what a pull cost are facts about the
 * deployment, and a customer report that mentioned any of them would be
 * promising a schedule we would then have to keep.
 */
export interface QueueRow {
  name: string
  queued: number
  active: number
  retrying: number
  failed: number
  completed: number
  oldestQueued: string | null
  oldestActive: string | null
  lastCompleted: string | null
}

export interface ScheduleRow {
  name: string
  cron: string
  timezone: string
}

export interface FailureRow {
  id: string
  name: string
  failedAt: string | null
  retryCount: number
  siteId: string | null
  message: string | null
}

export interface OpsData {
  queues: QueueRow[]
  schedules: ScheduleRow[]
  failures: FailureRow[]
  /** The server's clock, so an age is measured against the machine that owns
   * the queue rather than against whatever the reader's laptop thinks. */
  now: string
}

export interface FeedRow {
  siteId: string
  host: string
  organizationId: string
  organization: string
  searchLatestDate: string | null
  searchLastRunAt: string | null
  searchBackfilling: boolean
  searchError: string | null
  sitemapLastFetchedAt: string | null
  sitemapDocuments: number
  backlinkLastSyncedAt: string | null
  backlinkCappedAt: string | null
  backlinkBudgetCents: number | null
  backlinkSpentCents: number
  referringDomains: number
  serpLastCheckedAt: string | null
}

/** Which feed a trigger or a sweep drives. Carried from the api rather than
 * derived from the name here, because the allowlist is the only place that
 * knows, and a prefix rule in the interface would be a second one. */
export type FeedName = 'search' | 'sitemap' | 'backlinks' | 'actions' | 'outreach'

export interface TriggerSpec {
  name: string
  label: string
  feed: FeedName
}

export interface FeedsData {
  sites: FeedRow[]
  triggers: TriggerSpec[]
  sweeps: TriggerSpec[]
  now: string
}

export interface BacklinkConfig {
  enabled: boolean
  linkLevelEnabled: boolean
  monthlyBudgetCents: number
  periodStart: string
  lastSyncedAt: string | null
  lastCappedAt: string | null
  spentCents: number
}

/** Polled while the page is open: a backlog is only interesting as a thing that
 * is or is not moving, and a static number cannot show that. */
export function useOps() {
  return useQuery({
    queryKey: ['admin', 'ops'],
    refetchInterval: 10_000,
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: async () => unwrap<OpsData>(await api.api.admin.ops.$get()),
  })
}

export function useFeeds() {
  return useQuery({
    queryKey: ['admin', 'feeds'],
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
    queryFn: async () => unwrap<FeedsData>(await api.api.admin.feeds.$get()),
  })
}

export interface TriggerInput {
  siteId: string
  trigger: string
  /** Which checks to re-run. Absent is all of them, and the narrowing is part
   * of the job's identity — a dry run of one detector and a full paid scan are
   * different jobs. */
  types?: ActionTypeId[]
  /** Stops short of paying a model to describe what the scan finds. */
  dryRun?: boolean
}

export function useTriggerJob() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ siteId, trigger, types, dryRun }: TriggerInput) => {
      const response = await api.api.admin.sites[':siteId'].trigger.$post({
        param: { siteId },
        json: { trigger, ...(types && types.length > 0 ? { types } : {}), dryRun: dryRun ?? false },
      })
      return unwrap<{ queued: boolean; job: string }>(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ops'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'feeds'] })
    },
  })
}

export function useSetBacklinkBudget() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      siteId,
      ...patch
    }: {
      siteId: string
      monthlyBudgetCents?: number
      linkLevelEnabled?: boolean
    }) => {
      const response = await api.api.admin.sites[':siteId']['backlink-config'].$patch({
        param: { siteId },
        json: patch,
      })
      return unwrap<{ config: BacklinkConfig }>(response)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'feeds'] }),
  })
}

/**
 * The outreach feed's operational state, which exists only here.
 *
 * The customer's outreach screens read their own prospects and their own
 * settings; none of them can say whether the mail is arriving, what a sweep
 * cost, or that the site has stopped sending. Deliverability in particular has
 * no customer-facing counterpart by design — a bounce rate is a fact about a
 * sending domain's reputation, and the control that acts on it is staff's.
 */
export interface OutreachDiscoveryRunRow {
  startedAt: string | null
  finishedAt: string | null
  status: string | null
  keywordsRun: number
  resultsSeen: number
  prospectsCreated: number
  filtered: number
  error: string | null
}

export interface OutreachSiteRow {
  siteId: string
  host: string
  organizationId: string
  organization: string
  enabled: boolean | null
  dryRun: boolean | null
  dailyCap: number | null
  pausedAt: string | null
  pausedReason: string | null
  senderEmail: string | null
  smtpConfigured: boolean
  imapConfigured: boolean
  lastTickAt: string | null
  targets: number
  targetsEnabled: number
  discovery: OutreachDiscoveryRunRow | null
  prospects: number
  dismissedByMachine: number
  researched: number
  prospectsByStatus: Record<string, number>
  drafts: number
  sent: number
  sentDryRun: number
  failed: number
  replies: number
  repliesByClass: Record<string, number>
  won: number
}

export interface DeliverabilityRow {
  siteId: string
  host: string
  organizationId: string
  organization: string
  senderEmail: string | null
  sent: number
  /** Rehearsed sends. Kept apart from `sent` everywhere, including here: a
   * dry run is mail nobody received, and a bounce rate measured over it reads
   * healthy for a site with no mailbox at all. */
  sentDryRun: number
  failed: number
  bounced: number
  unsubscribed: number
  autoReplies: number
  replied: number
  interested: number
  won: number
  /** Null where nothing real was sent. */
  bounceRate: number | null
  lastSentAt: string | null
}

export interface SuppressionRow {
  organizationId: string
  organization: string
  total: number
  emails: number
  domains: number
  reasons: { reason: string; count: number }[]
}

export interface OutreachOpsData {
  days: number
  sites: OutreachSiteRow[]
  deliverability: DeliverabilityRow[]
  suppressions: SuppressionRow[]
  now: string
}

export function useOutreachOps() {
  return useQuery({
    queryKey: ['admin', 'outreach'],
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
    queryFn: async () => unwrap<OutreachOpsData>(await api.api.admin.outreach.$get({ query: {} })),
  })
}

export interface OutreachEfficacyCounts {
  contacted: number
  replied: number
  won: number
  /** Null under a denominator worth dividing by. Rendered as nothing rather
   * than as a percentage, the way `DeltaPill` drops a change it cannot compute. */
  replyRate: number | null
  winRate: number | null
  approved: number
  edited: number
  editedRate: number | null
}

export interface OutreachSourceEfficacyRow extends OutreachEfficacyCounts {
  source: OutreachSource
  label: string
  prospects: number
  /** Out of every denominator, because no mail was written for them — but shown,
   * because a source that is mostly dismissed is a finding. */
  vetoed: number
}

export interface OutreachVersionEfficacyRow extends OutreachEfficacyCounts {
  versionId: string
  prompt: string
  version: number
  active: boolean
}

export interface OutreachEfficacyData {
  sources: OutreachSourceEfficacyRow[]
  versions: OutreachVersionEfficacyRow[]
}

/**
 * Which source earns links, and which wording earns links.
 *
 * The outreach twin of `useEfficacy`, and empty by construction until real mail
 * has gone out — a rehearsed send counts for nothing here.
 */
export function useOutreachEfficacy() {
  return useQuery({
    queryKey: ['admin', 'outreach', 'efficacy'],
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => unwrap<OutreachEfficacyData>(await api.api.admin.outreach.efficacy.$get()),
  })
}

/** The two deployment-wide outreach fan-outs. Named rather than typed as a
 * string, for the reason `ActionSweep` is. */
export type OutreachSweep = 'outreach-source-all' | 'outreach-tick-all'

export function useOutreachSweep() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (sweep: OutreachSweep) => {
      const response = await api.api.admin.sweep.$post({ json: { sweep } })
      return unwrap<{ queued: boolean; job: string }>(response)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ops'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'outreach'] })
    },
  })
}

/**
 * Lets a paused site send again.
 *
 * Staff's alone. The pause is written against a bounce rate the site itself
 * measured, and clearing it is a decision somebody makes after reading why —
 * which is why the control lives beside the rate rather than on the customer's
 * own settings page.
 */
export function useClearOutreachPause() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (siteId: string) => {
      const response = await api.api.admin.sites[':siteId'].outreach['clear-pause'].$post({
        param: { siteId },
      })
      return unwrap<{ config: { siteId: string } }>(response)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'outreach'] }),
  })
}
